import { formatDuration, formatTimestamp } from '../../../lib/formatUtils';
import { formatWeldDataValue } from '../../../utils/unitUtils';
import type { WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import type { PassViewRecord } from './fetchWeldViewApis';

export type ProductionPassStatus = 'Completed' | 'In Progress' | 'Rejected' | 'Failed';
export type BinaryPassStatus = 'Completed' | 'Failed';

/** Friendly label for the project’s active WPS status calculation method. */
export function formatStatusMethodLabel(method?: string | null): string {
  const raw = String(method ?? '').trim();
  if (!raw) return 'project method';
  const normalized = raw.toLowerCase().replace(/[\s_-]+/g, '');
  switch (normalized) {
    case 'instantaneous':
      return 'instant reading';
    case 'avgbyzone':
    case 'zone':
      return 'zone average';
    case 'avgbypass':
    case 'pass':
      return 'pass average';
    case 'avgbytilt':
    case 'tilt':
      return 'tilt average';
    default:
      return raw;
  }
}

/** Human-readable label for raw WPS status codes in drill-down UI. */
export function formatWpsRawStatusDisplay(value?: string | boolean | null): string {
  if (value == null || value === '') return '—';
  if (value === true) return 'Passed';
  if (value === false) return 'Out of limit';
  const normalized = String(value).toUpperCase();
  switch (normalized) {
    case 'TRUE':
      return 'Passed';
    case 'FALSE':
      return 'Out of limit';
    case 'NULL':
      return 'Rejected';
    case 'CROSS':
      return 'Failed';
    default:
      return String(value);
  }
}

/** Parse API epoch seconds (string or number) to milliseconds. */
export function parsePassUnixSeconds(value?: string | number | null): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < 1e12 ? n * 1000 : n;
}

export function sortPassesByStartTime(passes: PassViewRecord[]): PassViewRecord[] {
  return [...passes].sort((a, b) => {
    const aMs = parsePassUnixSeconds(a.startTime);
    const bMs = parsePassUnixSeconds(b.startTime);
    if (aMs === null && bMs === null) return 0;
    if (aMs === null) return 1;
    if (bMs === null) return -1;
    return aMs - bMs;
  });
}

export function formatPassTimestampDisplay(value?: string | number | null): string {
  const ms = parsePassUnixSeconds(value);
  if (ms === null) return '—';
  return formatTimestamp(ms / 1000);
}

function normalizeDurationDisplay(value: string | number): string {
  const formatted = formatDuration(value);
  const text = typeof formatted === 'string' ? formatted : String(formatted);
  return text === '-' ? '—' : text;
}

export function formatPassDurationDisplay(pass: PassViewRecord): string {
  if (pass.weldTimeMinutes != null && pass.weldTimeMinutes > 0) {
    return normalizeDurationDisplay(pass.weldTimeMinutes);
  }
  // Do not fall back to start→end wall-clock; that is session span, not weld duration.
  return '—';
}

function isNullStatus(value?: string | null): boolean {
  if (!value) return false;
  return value.toUpperCase() === 'NULL';
}

function isFalseStatus(value?: string | null): boolean {
  if (!value) return false;
  return value.toUpperCase() === 'FALSE';
}

function isCrossStatus(value?: string | null): boolean {
  if (!value) return false;
  return value.toUpperCase() === 'CROSS';
}

/** True when the pass has a valid start and end timestamp. */
export function isPassEnded(pass: PassViewRecord): boolean {
  const startMs = parsePassUnixSeconds(pass.startTime);
  const endMs = parsePassUnixSeconds(pass.endTime);
  return startMs !== null && endMs !== null && endMs > startMs;
}

/** Mirror production WeldData saga pass status derivation. */
export function deriveProductionPassStatus(pass: PassViewRecord): ProductionPassStatus {
  const instantaneous = pass.passStatusInstantaneous ?? pass.status;
  const zone = pass.passStatusZone;
  const avg = pass.passAvgStatus;

  if (isCrossStatus(instantaneous) || isCrossStatus(zone) || isCrossStatus(avg)) {
    return 'Failed';
  }

  if (
    isNullStatus(instantaneous)
    || isNullStatus(zone)
    || isNullStatus(avg)
  ) {
    return 'Rejected';
  }

  if (
    isFalseStatus(instantaneous)
    || isFalseStatus(zone)
    || isFalseStatus(avg)
  ) {
    return 'In Progress';
  }

  return 'Completed';
}

/**
 * Out-of-limit drill-down: completed passes with FALSE status are treated as failed
 * (production maps FALSE → In Progress even when the pass has ended).
 */
export function deriveDrillDownPassStatus(pass: PassViewRecord): ProductionPassStatus {
  const base = deriveProductionPassStatus(pass);
  if (base === 'In Progress' && isPassEnded(pass)) {
    return 'Failed';
  }
  return base;
}

/**
 * Weld-dashboard drill-down pass summary: only Completed | Failed.
 * TRUE / clean → Completed; FALSE, CROSS, NULL/Rejected, In Progress → Failed.
 */
export function deriveBinaryPassStatus(pass: PassViewRecord): BinaryPassStatus {
  return deriveProductionPassStatus(pass) === 'Completed' ? 'Completed' : 'Failed';
}

/** Short CW / CCW label for pass bug type (API may send Clockwise, Counterclockwise, etc.). */
export function formatBugTypeDisplay(raw?: string | null): string {
  if (raw == null || String(raw).trim() === '' || raw === '-') return '—';
  const v = String(raw).trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (v === 'cw' || v === 'clockwise' || (v.includes('clock') && !v.includes('counter') && !v.includes('anti'))) {
    return 'CW';
  }
  if (
    v === 'ccw'
    || v === 'counterclockwise'
    || v.includes('counter')
    || v.includes('anticlock')
    || v.includes('anti')
  ) {
    return 'CCW';
  }
  return String(raw).trim();
}

function formatPipeDimensionWithUnit(value: string | number | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /[a-zA-Z]/.test(value)) {
    return value.trim();
  }
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(n)) return String(value);
  return formatWeldDataValue(n, 'pipe_diameter');
}

export function formatDrillDownPipeSizeLabel(
  row: Pick<WeldDrillDownRow, 'pipeDiameter' | 'wallThickness'>,
  pipeDisplayNameFallback?: string | null,
): string {
  const diameter = formatPipeDimensionWithUnit(row.pipeDiameter);
  const wall = formatPipeDimensionWithUnit(row.wallThickness);
  if (diameter && wall) return `${diameter} × ${wall}`;
  if (diameter) return diameter;
  if (wall) return wall;
  const fallback = pipeDisplayNameFallback?.trim();
  if (fallback) return fallback;
  return '—';
}

export function passStatusBadgeClass(status: ProductionPassStatus | BinaryPassStatus): string {
  switch (status) {
    case 'Completed':
      return 'border border-[#C8E6C9] bg-[#E8F5E9] text-[#2E7D32]';
    case 'In Progress':
      return 'border border-[#FFE0B2] bg-[#FFF3E0] text-[#E65100]';
    case 'Rejected':
    case 'Failed':
      return 'border border-hero-red-20 bg-hero-red-10 text-readable-red';
    default:
      return 'border border-[#E2E5EA] bg-page-bg text-text-grey';
  }
}

export type IndicationPassOption = {
  id: string | number;
  name: string;
  label: string;
};

export const ENTIRE_WELD_SCOPE_ID = '__entire_weld__';

export function isEntireWeldScope(scopeId: string): boolean {
  return scopeId === ENTIRE_WELD_SCOPE_ID;
}

function formatPassOccurrenceLabel(occurrence: number): string {
  if (occurrence <= 1) return '';
  if (occurrence === 2) return '(2nd)';
  if (occurrence === 3) return '(3rd)';
  return `(${occurrence}th)`;
}

/** One dropdown row per physical pass; disambiguates repeated pass_name values. */
export function buildIndicationPassOptions(
  passes: PassViewRecord[],
): IndicationPassOption[] {
  const sorted = sortPassesByStartTime(passes);
  const nameCounts = new Map<string, number>();

  return sorted.map((pass) => {
    const name = pass.name?.trim() || `Pass ${pass.id}`;
    const occurrence = (nameCounts.get(name) ?? 0) + 1;
    nameCounts.set(name, occurrence);

    const disambiguators: string[] = [];
    if (pass.torch?.trim()) disambiguators.push(pass.torch.trim());
    if (pass.stationNumber?.trim()) {
      disambiguators.push(`Station ${pass.stationNumber.trim()}`);
    }
    if (pass.jobNumber?.trim() && sorted.some((p) => p.jobNumber !== pass.jobNumber)) {
      disambiguators.push(pass.jobNumber.trim());
    }
    const occurrenceLabel = formatPassOccurrenceLabel(occurrence);
    if (occurrenceLabel) disambiguators.push(occurrenceLabel);

    const label =
      disambiguators.length > 0
        ? `${name} · ${disambiguators.join(' · ')}`
        : name;

    return { id: pass.id, name, label };
  });
}

/** Entire-weld scope first, then one row per physical pass. */
export function buildIndicationScopeOptions(
  passes: PassViewRecord[],
): IndicationPassOption[] {
  return [
    {
      id: ENTIRE_WELD_SCOPE_ID,
      name: 'Entire weld',
      label: 'Entire weld (all passes)',
    },
    ...buildIndicationPassOptions(passes),
  ];
}
